/**
 * Column editor popover for configurable tables.
 *
 * Allows users to toggle column visibility, reorder columns via
 * drag-and-drop, and manage named column configurations (localStorage).
 * Column configurations capture columns + sort only — never filters. The
 * filter-aware, shareable concept is a database-backed "Saved View", handled
 * at the page level, not here.
 */

import { useState, useMemo } from "react";
import type { ReactNode } from "react";
import { Popover } from "../../Popover/Popover";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import type { DragEndEvent } from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GearSixIcon } from "@phosphor-icons/react/GearSix";
import { FloppyDiskIcon } from "@phosphor-icons/react/FloppyDisk";
import { CopyIcon } from "@phosphor-icons/react/Copy";
import { TrashIcon } from "@phosphor-icons/react/Trash";
import { EyeIcon } from "@phosphor-icons/react/Eye";
import { EyeSlashIcon } from "@phosphor-icons/react/EyeSlash";
import { Icon } from "../../Icons/Icon";
import type { SavedColumnConfiguration } from "./columnConfigurations";
import {
  NameInputForm_,
  ConfigurationItem_,
} from "./ColumnEditorConfigurations";

function SortableColumnItem_({
  id,
  header,
  checked,
  onToggle,
}: {
  id: string;
  header: ReactNode;
  checked: boolean;
  onToggle: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="group flex cursor-default items-center gap-3 py-2"
    >
      <span
        {...attributes}
        {...listeners}
        className="cursor-grab text-content-faint group-hover:text-content-muted"
      >
        <svg width="10" height="14" viewBox="0 0 10 14" fill="currentColor">
          <circle cx="2" cy="2" r="1.5" />
          <circle cx="8" cy="2" r="1.5" />
          <circle cx="2" cy="7" r="1.5" />
          <circle cx="8" cy="7" r="1.5" />
          <circle cx="2" cy="12" r="1.5" />
          <circle cx="8" cy="12" r="1.5" />
        </svg>
      </span>
      <input
        type="checkbox"
        checked={checked}
        onChange={onToggle}
        className="h-5 w-5 cursor-pointer rounded border-2 border-border accent-blue"
      />
      <span className="text-sm font-bold text-foreground">{header}</span>
    </div>
  );
}

type FormMode = "none" | "save" | "duplicate";

// oxlint-disable-next-line complexity -- grandfathered, see ENG-7672
export function ColumnEditorPopover_({
  columns,
  hiddenKeys,
  columnOrder,
  onToggle,
  onReorder,
  onSaveDefault,
  actionsHidden,
  onToggleActions,
  configurations,
  activeConfigId,
  onSaveConfiguration,
  onUpdateConfiguration,
  onLoadConfiguration,
  onDeleteConfiguration,
  onDuplicateConfiguration,
  isDirty,
}: {
  columns: { key: string; header: ReactNode }[];
  hiddenKeys: Set<string>;
  columnOrder: string[];
  onToggle: (key: string) => void;
  onReorder: (order: string[]) => void;
  onSaveDefault?: (() => void) | undefined;
  /** Whether the table's actions slot is currently hidden. When provided
   *  alongside `onToggleActions`, the popover renders a Show/Hide Actions
   *  button in the options section. */
  actionsHidden?: boolean | undefined;
  onToggleActions?: (() => void) | undefined;
  configurations?: SavedColumnConfiguration[] | undefined;
  activeConfigId?: string | undefined;
  onSaveConfiguration?: ((name: string) => void) | undefined;
  onUpdateConfiguration?: ((configId: string) => void) | undefined;
  onLoadConfiguration?: ((configId: string) => void) | undefined;
  onDeleteConfiguration?: ((configId: string) => void) | undefined;
  onDuplicateConfiguration?: ((name: string) => void) | undefined;
  isDirty?: boolean | undefined;
}) {
  const [formMode, setFormMode] = useState<FormMode>("none");
  const [search, setSearch] = useState("");
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const orderedColumns = useMemo(() => {
    const colMap = new Map(columns.map((c) => [c.key, c]));
    const ordered: { key: string; header: ReactNode }[] = [];
    for (const key of columnOrder) {
      const col = colMap.get(key);
      if (col) ordered.push(col);
    }
    for (const col of columns) {
      if (!columnOrder.includes(col.key)) ordered.push(col);
    }
    return ordered;
  }, [columns, columnOrder]);

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = orderedColumns.findIndex((c) => c.key === active.id);
    const newIndex = orderedColumns.findIndex((c) => c.key === over.id);
    const newOrder = arrayMove(
      orderedColumns.map((c) => c.key),
      oldIndex,
      newIndex,
    );
    onReorder(newOrder);
  }

  const filteredColumns = useMemo(() => {
    if (!search) return orderedColumns;
    const lower = search.toLowerCase();
    return orderedColumns.filter((col) => {
      const text =
        typeof col.header === "string"
          ? col.header
          : col.key.replace(/([A-Z])/g, " $1");
      return text.toLowerCase().includes(lower);
    });
  }, [orderedColumns, search]);

  const activeConfig = configurations?.find(
    (c) => c.configId === activeConfigId,
  );
  const hasConfigurations = onSaveConfiguration !== undefined;

  return (
    <Popover>
      <Popover.Trigger
        className={
          // Match the header text colour (foreground) so the gear reads as part
          // of the header row, not a faint afterthought.
          "inline-flex h-7 w-7 items-center justify-center rounded-md text-foreground transition-all hover:scale-110 hover:bg-muted"
        }
        title="Modify columns"
        data-testid="table-column-editor"
      >
        <Icon icon={GearSixIcon} size="sm" />
      </Popover.Trigger>
      <Popover.Content align="end" sideOffset={8}>
        <div className="w-72">
          <div className="px-4 pt-3 pb-1">
            <h3 className="text-sm font-bold text-foreground">Columns</h3>
          </div>
          {columns.length > 8 && (
            <div className="px-4 pb-1">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Filter columns…"
                className="w-full rounded-md border border-border bg-transparent px-2.5 py-1.5 text-xs text-foreground placeholder:text-content-faint focus:border-blue focus:outline-none"
              />
            </div>
          )}
          <div className="max-h-60 overflow-y-auto px-4">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={orderedColumns.map((c) => c.key)}
                strategy={verticalListSortingStrategy}
              >
                {filteredColumns.map((col) => (
                  <SortableColumnItem_
                    key={col.key}
                    id={col.key}
                    header={col.header}
                    checked={!hiddenKeys.has(col.key)}
                    onToggle={() => onToggle(col.key)}
                  />
                ))}
              </SortableContext>
            </DndContext>
          </div>
          {(onSaveDefault || onToggleActions) && (
            <div className="flex flex-col gap-1.5 border-t border-border px-4 py-2.5">
              {onToggleActions && (
                <button
                  type="button"
                  onClick={onToggleActions}
                  className="flex items-center gap-1.5 text-xs font-medium text-content-secondary transition-colors hover:text-foreground"
                >
                  <Icon
                    icon={actionsHidden ? EyeIcon : EyeSlashIcon}
                    size="xs"
                  />
                  {actionsHidden ? "Show Actions" : "Hide Actions"}
                </button>
              )}
              {onSaveDefault && (
                <button
                  type="button"
                  onClick={onSaveDefault}
                  className="flex items-center gap-1.5 text-xs font-medium text-content-secondary transition-colors hover:text-foreground"
                >
                  <Icon icon={FloppyDiskIcon} size="xs" />
                  Save as My Default
                </button>
              )}
            </div>
          )}
          {hasConfigurations && (
            <div className="border-t border-border px-4 py-2.5">
              <div className="mb-1.5 text-[10px] font-bold tracking-wider text-content-muted uppercase">
                Column Configurations
              </div>
              {configurations && configurations.length > 0 ? (
                <div className="mb-2">
                  {configurations.map((configuration) => (
                    <ConfigurationItem_
                      key={configuration.configId}
                      configuration={configuration}
                      isActive={configuration.configId === activeConfigId}
                      onLoad={() =>
                        onLoadConfiguration?.(configuration.configId)
                      }
                    />
                  ))}
                </div>
              ) : (
                <div className="mb-2 text-xs text-content-muted">
                  No saved configurations
                </div>
              )}
              {formMode === "save" && (
                <NameInputForm_
                  placeholder="Configuration name..."
                  submitLabel="Save"
                  onSubmit={(name) => {
                    onSaveConfiguration?.(name);
                    setFormMode("none");
                  }}
                  onCancel={() => setFormMode("none")}
                />
              )}
              {formMode === "duplicate" && (
                <NameInputForm_
                  placeholder={`${activeConfig?.configName ?? ""} (copy)`}
                  submitLabel="Duplicate"
                  onSubmit={(name) => {
                    onDuplicateConfiguration?.(name);
                    setFormMode("none");
                  }}
                  onCancel={() => setFormMode("none")}
                />
              )}
              {formMode === "none" && (
                <>
                  <div className="mt-1.5 mb-1 text-[10px] font-bold tracking-wider text-content-muted uppercase">
                    Options
                  </div>
                  <div className="flex flex-col gap-1">
                    {activeConfig && isDirty && onUpdateConfiguration && (
                      <button
                        type="button"
                        onClick={() =>
                          onUpdateConfiguration(activeConfig.configId)
                        }
                        className="flex items-center gap-1.5 text-xs font-medium text-content-secondary transition-colors hover:text-foreground"
                      >
                        <Icon icon={FloppyDiskIcon} size="xs" />
                        Update &ldquo;{activeConfig.configName}&rdquo;
                      </button>
                    )}
                    {activeConfig && onDuplicateConfiguration && (
                      <button
                        type="button"
                        onClick={() => setFormMode("duplicate")}
                        className="flex items-center gap-1.5 text-xs font-medium text-content-secondary transition-colors hover:text-foreground"
                      >
                        <Icon icon={CopyIcon} size="xs" />
                        Duplicate Configuration
                      </button>
                    )}
                    {(!activeConfig || isDirty) && (
                      <button
                        type="button"
                        onClick={() => setFormMode("save")}
                        className="flex items-center gap-1.5 text-xs font-medium text-content-secondary transition-colors hover:text-foreground"
                      >
                        <Icon icon={FloppyDiskIcon} size="xs" />
                        {activeConfig
                          ? "Save as New Configuration"
                          : "Save Configuration"}
                      </button>
                    )}
                    {activeConfig && onDeleteConfiguration && (
                      <button
                        type="button"
                        onClick={() =>
                          onDeleteConfiguration(activeConfig.configId)
                        }
                        className="flex items-center gap-1.5 text-xs font-medium text-red/50 transition-colors hover:text-red"
                      >
                        <Icon icon={TrashIcon} size="xs" />
                        Delete this configuration
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </Popover.Content>
    </Popover>
  );
}
